import { memo, type FC, type ReactNode } from "react";
import { Check, Mail, MessageCircle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { formatNumber } from "src/utils/numberFormat";
import type { CampaignChannel } from "src/layouts/Engagements/EngagementInterfaces";
import { AUDIENCE_ROUTE, CREATE_CAMPAIGN_ROUTE } from "./createList.constants";
import { useCreateListLabel } from "./useCreateListLabel";

interface ChannelOptionProps {
  icon: ReactNode;
  title: string;
  reach: string;
  disabled: boolean;
  onStart: () => void;
}

const ChannelOption: FC<ChannelOptionProps> = ({ icon, title, reach, disabled, onStart }) => {
  const label = useCreateListLabel();
  return (
    <div className="create-list__channel">
      <span className="create-list__channel-icon" aria-hidden>
        {icon}
      </span>
      <h3 className="create-list__channel-title">{title}</h3>
      <p className="create-list__channel-copy">{reach}</p>
      <p className="create-list__channel-copy">{label("ready.preselected")}</p>
      <button
        type="button"
        className="button button--outline campaign-flow__action create-list__channel-start"
        onClick={onStart}
        disabled={disabled}
      >
        {label("ready.start")}
      </button>
    </div>
  );
};

interface ListReadyScreenProps {
  listName: string;
  listMeta: string;
  peopleCount: number;
  phoneCount: number;
  emailCount?: number;
  labelKey?: string;
}

const ListReadyScreen: FC<ListReadyScreenProps> = ({
  listName,
  listMeta,
  peopleCount,
  phoneCount,
  emailCount = peopleCount,
  labelKey,
}) => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const label = useCreateListLabel(labelKey);
  const formatValue = (value: number) => formatNumber(value, {}, i18n.language);

  const startCampaign = (channel: CampaignChannel) =>
    navigate(CREATE_CAMPAIGN_ROUTE, { state: { channel, list_name: listName } });

  return (
    <>
      <section className="campaign-flow__card create-list__ready">
        <span className="create-list__ready-badge" aria-hidden>
          <Check size={22} strokeWidth={3} />
        </span>
        <h2 className="create-list__ready-title" dir="auto">
          {label("ready.title", { name: listName })}
        </h2>
        <p className="create-list__ready-meta">
          {label("ready.people", { count: peopleCount, value: formatValue(peopleCount) })} · {listMeta}
        </p>

        <div className="create-list__ready-next">
          <h3 className="create-list__ready-question">{label("ready.question")}</h3>
          <div className="create-list__channels">
            <ChannelOption
              icon={<Mail size={18} />}
              title={label("ready.emailTitle")}
              reach={label("ready.emailReach", { count: emailCount, value: formatValue(emailCount) })}
              disabled={emailCount === 0}
              onStart={() => startCampaign("email")}
            />
            <ChannelOption
              icon={<MessageCircle size={18} />}
              title={label("ready.whatsappTitle")}
              reach={label("ready.whatsappReach", { count: phoneCount, value: formatValue(phoneCount) })}
              disabled={phoneCount === 0}
              onStart={() => startCampaign("whatsapp")}
            />
          </div>
        </div>

        <p className="create-list__ready-footnote">{label("ready.footnote")}</p>
      </section>

      <div className="campaign-flow__actions create-list__ready-actions">
        <button
          type="button"
          className="button button--primary campaign-flow__action"
          onClick={() => navigate(AUDIENCE_ROUTE)}
        >
          {label("ready.backToAudience")}
        </button>
      </div>
    </>
  );
};

export default memo(ListReadyScreen);
